import { useCallback, useEffect, useMemo, useState } from "react";
import {
  listarMaterias,
  crearMateria,
  actualizarMateria,
  eliminarMateria,
} from "../../../../services/materiasService";
import { showToast } from "../../../../utils/showToast";

const POR_PAGINA = 10;

const buildTempId = (() => {
  let counter = 0;
  return () => `temp-materia-${counter++}`;
})();

const normalizeMateria = (materia = {}, fallbackId) => ({
  id: materia.id ?? fallbackId ?? buildTempId(),
  nombre: materia.nombre?.trim() || "Sin nombre",
  sigla: materia.sigla?.trim() || "",
  descripcion: materia.descripcion?.trim() || "",
  color_hex: materia.color_hex?.toUpperCase() || "#000000",
  activo:
    typeof materia.activo === "boolean"
      ? materia.activo
      : materia.activo === 1 || materia.activo === "1",
});

const initialFormState = {
  nombre: "",
  sigla: "",
  descripcion: "",
  color_hex: "#000000",
  activo: true,
};

const buildPayload = (form) => ({
  nombre: form.nombre?.trim() || "",
  sigla: form.sigla?.trim() || null,
  descripcion: form.descripcion?.trim() || null,
  color_hex: form.color_hex?.toUpperCase() || null,
  activo: form.activo ? 1 : 0,
});

const validarMateria = (form) => {
  if (!form.nombre?.trim()) return "El nombre de la materia es obligatorio.";
  
  // Validar color hexadecimal si se proporciona
  if (form.color_hex && !/^#[0-9A-F]{6}$/i.test(form.color_hex)) {
    return "El color debe ser un código hexadecimal válido (ej: #FF5733).";
  }
  
  return null;
};

const extractMateria = (response, fallback) => {
  const base = response?.data ?? response?.materia ?? response ?? {};
  const merged = fallback ? { ...fallback, ...base } : base;
  return normalizeMateria(merged, fallback?.id);
};

export default function useMateriaLogic() {
  const [materias, setMaterias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [modal, setModal] = useState("");
  const [materiaSeleccionada, setMateriaSeleccionada] = useState(null);
  const [formNuevo, setFormNuevo] = useState(initialFormState);
  const [formEditar, setFormEditar] = useState(initialFormState);
  const [savingNuevo, setSavingNuevo] = useState(false);
  const [savingEditar, setSavingEditar] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [filtro, setFiltro] = useState("total");
  const [busqueda, setBusqueda] = useState("");
  const [pagina, setPagina] = useState(1);

  const fetchMaterias = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listarMaterias();
      const normalized = (Array.isArray(data) ? data : []).map(normalizeMateria);
      setMaterias(normalized);
      setListError(null);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Error fetching materias:", err);
      setListError(err?.message || "Error al cargar materias");
      setMaterias([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMaterias();
  }, [fetchMaterias]);

  const resumen = useMemo(() => {
    const total = materias.length;
    const activas = materias.filter((m) => m.activo).length;
    const inactivas = total - activas;
    return { total, activas, inactivas };
  }, [materias]);

  // Resetear página al cambiar filtro o búsqueda
  useEffect(() => { setPagina(1); }, [filtro, busqueda]);

  const materiasFiltered = useMemo(() => {
    let lista = materias;
    if (filtro === "activas")   lista = lista.filter((m) => m.activo);
    if (filtro === "inactivas") lista = lista.filter((m) => !m.activo);
    if (busqueda.trim()) {
      const q = busqueda.trim().toLowerCase();
      lista = lista.filter((m) =>
        m.nombre.toLowerCase().includes(q) ||
        (m.sigla && m.sigla.toLowerCase().includes(q))
      );
    }
    return lista;
  }, [materias, filtro, busqueda]);

  const totalFiltradas = materiasFiltered.length;
  const totalPaginas   = Math.max(1, Math.ceil(totalFiltradas / POR_PAGINA));

  const materiasPaginadas = useMemo(() => {
    const start = (pagina - 1) * POR_PAGINA;
    return materiasFiltered.slice(start, start + POR_PAGINA);
  }, [materiasFiltered, pagina]);

  const abrirModalNuevo = useCallback(() => {
    setFormNuevo(initialFormState);
    setMateriaSeleccionada(null);
    setModal("crear");
  }, []);

  const abrirModalEditar = useCallback((materia) => {
    setMateriaSeleccionada(materia);
    setFormEditar({
      nombre: materia.nombre,
      sigla: materia.sigla,
      descripcion: materia.descripcion,
      color_hex: materia.color_hex,
      activo: materia.activo,
    });
    setModal("editar");
  }, []);

  const abrirModalEliminar = useCallback((materia) => {
    setMateriaSeleccionada(materia);
    setModal("eliminar");
  }, []);

  const cerrarModal = useCallback(() => {
    setModal("");
    setMateriaSeleccionada(null);
    setFormNuevo(initialFormState);
    setFormEditar(initialFormState);
  }, []);

  const handleCrearMateria = useCallback(async () => {
    const error = validarMateria(formNuevo);
    if (error) {
      showToast({ type: "danger", message: error });
      return;
    }

    setSavingNuevo(true);
    try {
      const payload = buildPayload(formNuevo);
      const response = await crearMateria(payload);
      const nuevaMateria = extractMateria(response, null);

      setMaterias((prev) => [nuevaMateria, ...prev]);
      showToast({ type: "success", message: response?.message || "Materia creada exitosamente" });
      cerrarModal();
    } catch (err) {
      console.error("Error creating materia:", err);
      showToast({ type: "danger", message: err?.message || "Error al crear materia" });
    } finally {
      setSavingNuevo(false);
    }
  }, [formNuevo, cerrarModal]);

  const handleActualizarMateria = useCallback(async () => {
    const error = validarMateria(formEditar);
    if (error) {
      showToast({ type: "danger", message: error });
      return;
    }

    setSavingEditar(true);
    try {
      const payload = buildPayload(formEditar);
      const response = await actualizarMateria(materiaSeleccionada.id, payload);
      const materiaActualizada = extractMateria(response, materiaSeleccionada);

      setMaterias((prev) =>
        prev.map((m) => (m.id === materiaSeleccionada.id ? materiaActualizada : m))
      );
      showToast({ type: "success", message: response?.message || "Materia actualizada exitosamente" });
      cerrarModal();
    } catch (err) {
      console.error("Error updating materia:", err);
      showToast({ type: "danger", message: err?.message || "Error al actualizar materia" });
    } finally {
      setSavingEditar(false);
    }
  }, [formEditar, materiaSeleccionada, cerrarModal]);

  const handleEliminarMateria = useCallback(async () => {
    setDeleting(true);
    try {
      const response = await eliminarMateria(materiaSeleccionada.id);
      setMaterias((prev) => prev.filter((m) => m.id !== materiaSeleccionada.id));
      showToast({ type: "success", message: response?.message || "Materia eliminada exitosamente" });
      cerrarModal();
    } catch (err) {
      console.error("Error deleting materia:", err);
      showToast({ type: "danger", message: err?.message || "Error al eliminar materia" });
    } finally {
      setDeleting(false);
    }
  }, [materiaSeleccionada, cerrarModal]);

  const aplicarFiltro = useCallback((tipo) => {
    setFiltro(tipo);
  }, []);

  return {
    materias: materiasPaginadas,
    busqueda,
    setBusqueda,
    pagina,
    setPagina,
    totalFiltradas,
    totalPaginas,
    porPagina: POR_PAGINA,
    loading,
    listError,
    lastUpdated,
    resumen,
    filtro,
    modal,
    materiaSeleccionada,
    formNuevo,
    setFormNuevo,
    formEditar,
    setFormEditar,
    savingNuevo,
    savingEditar,
    deleting,
    fetchMaterias,
    abrirModalNuevo,
    abrirModalEditar,
    abrirModalEliminar,
    cerrarModal,
    handleCrearMateria,
    handleActualizarMateria,
    handleEliminarMateria,
    aplicarFiltro,
  };
}
